import PropTypes from 'prop-types';
import React from 'react';
import { merge } from 'lodash';
import styled from 'styled-components';

const defaultStyles = {
  loaderWidth: 10,
  loaderHeight: 10,
  loaderColor: 'black',
  backgroundBorderRadius: '0px',
  backgroundColor: 'rgba(255, 255, 255, 0.75)',
  zIndex: 0,
};

const OverlayLoader = React.memo(
  ({ className, isLoading, children, styles, ...props }) => {
    const extendedStyles = merge(defaultStyles, styles);
    return (
      <StyledOverlay {...props} className={className} isLoading={isLoading}>
        {children}
        {isLoading && (
          <StyledBackground
            backgroundBorderRadius={extendedStyles.backgroundBorderRadius}
            backgroundColor={extendedStyles.backgroundColor}
            zIndex={extendedStyles.zIndex}
          >
            <StyledLoader
              loaderWidth={extendedStyles.loaderWidth}
              loaderHeight={extendedStyles.loaderHeight}
              loaderColor={extendedStyles.loaderColor}
            >
              <div />
              <div />
              <div />
              <div />
            </StyledLoader>
          </StyledBackground>
        )}
      </StyledOverlay>
    );
  }
);

OverlayLoader.displayName = 'OverlayLoader';

export const StyledOverlay = styled.div`
  position: relative;
`;

export const StyledBackground = styled.div`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  top: 0;
  background-color: ${({ backgroundColor }) => backgroundColor};
  border-radius: ${({ backgroundBorderRadius }) => backgroundBorderRadius};
  z-index: ${({ zIndex }) => zIndex};
  width: 100%;
  height: 100%;
`;

export const StyledLoader = styled.div`
  display: inline-block;
  position: relative;
  
  div {
    position: absolute;
    top: ${({ loaderHeight }) => -(loaderHeight / 2)}px;
    width: ${({ loaderWidth }) => loaderWidth}px;
    height: ${({ loaderHeight }) => loaderHeight}px;
    border-radius: 50%;
    background: ${({ loaderColor }) => loaderColor};
    animation-timing-function: cubic-bezier(0, 1, 1, 0);
    
    &:nth-child(1) {
      left: ${({ loaderWidth }) => -loaderWidth * 2.4}px;
      animation: lds-ellipsis1 0.6s infinite;
    }
    
    &:nth-child(2) {
      left: ${({ loaderWidth }) => -loaderWidth * 2.4}px;
      animation: lds-ellipsis2 0.6s infinite;
    }
    
    &:nth-child(3) {
      left: ${({ loaderWidth }) => -loaderWidth / 1.5}px;
      animation: lds-ellipsis2 0.6s infinite;
    }
    
    &:nth-child(4) {
      left: ${({ loaderWidth }) => -loaderWidth / 1.5}px;
      animation: lds-ellipsis3 0.6s infinite;
    }
    
    @keyframes lds-ellipsis1 {
      0% {
        transform: scale(0);
       }
      100% {
        transform: scale(1);
      }
    }
    
    @keyframes lds-ellipsis3 {
      0% {
        transform: scale(1);
      }
      100% {
        transform: scale(0);
    }
  }
  
  @keyframes lds-ellipsis2 {
    0% {
      transform: translate(0, 0);
    }
    100% {
      transform: translate(${({ loaderWidth }) => loaderWidth * 2}px, 0);
    }
  }
`;

OverlayLoader.propTypes = {
  className: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
  styles: PropTypes.objectOf({
    loaderWidth: PropTypes.number,
    loaderHeight: PropTypes.number,
    loaderColor: PropTypes.string,
    backgroundBorderRadius: PropTypes.string,
    backgroundColor: PropTypes.string,
  }),
};

OverlayLoader.defaultProps = {
  className: '',
  styles: defaultStyles,
};

export default OverlayLoader;
